import { useId, useState, type FormEvent } from 'react'
import { ASSISTANT_CORRECTION_MAX_CHARS, THREAD_REPLY_MAX_CHARS } from '../lib/response-plan'
import { dateTime as formatThreadTime } from '../lib/format'
import type { ThreadReply } from '../types'
import { Button, Dialog, Field, StatusBadge } from './ui'
import { ArrowSquareOut, AssistantIcon, Eye, EyeOff, PencilSimple, Reply, SpinnerGap, UserCircle } from './ui/icons'

export { dateTime as formatThreadTime } from '../lib/format'

export interface ThreadRepliesProps {
  replies: ThreadReply[]
  /** What the thread is about, for screen readers, for example "Alison Johnson's note on Member V3". */
  label: string
  canReply: boolean
  /** Prentus admins: correct or hide a Prentus Assistant reply. */
  canModerate?: boolean
  /** The Prentus Assistant is writing an answer in this thread now. */
  assistantPending?: boolean
  onReply?: (body: string) => Promise<boolean>
  onModerate?: (replyId: string, change: { body?: string; hidden?: boolean }) => Promise<boolean>
}

/**
 * The replies under one note, from the customer, from Prentus and from the
 * Prentus Assistant (Rod, 2026-11-04). An Assistant reply is always labeled
 * as AI and lists the sources it used. A Prentus admin can correct or hide it.
 */
export function ThreadReplies({ replies, label, canReply, canModerate = false, assistantPending = false, onReply, onModerate }: ThreadRepliesProps) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<ThreadReply | null>(null)
  const fieldId = useId()
  const trimmed = text.trim()

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!onReply || busy) return
    if (!trimmed) { setError('Write a reply before you send it.'); return }
    if (trimmed.length > THREAD_REPLY_MAX_CHARS) { setError(`Keep the reply under ${THREAD_REPLY_MAX_CHARS.toLocaleString('en-US')} characters.`); return }
    setError('')
    setBusy(true)
    const sent = await onReply(trimmed)
    setBusy(false)
    if (sent) { setText(''); setOpen(false) }
  }

  return (
    <div className="thread">
      {replies.length > 0 && (
        <ol className="thread-replies" aria-label={`Replies to ${label}`}>
          {replies.map((reply) => (
            <li key={reply.id} className={`thread-reply thread-reply--${reply.authorSide}${reply.hidden ? ' is-hidden' : ''}`}>
              <div className="thread-reply-head">
                <span className="thread-avatar" aria-hidden="true">{reply.authorSide === 'assistant' ? <AssistantIcon size={16} /> : <UserCircle size={16} />}</span>
                <strong>{reply.authorName}</strong>
                {reply.authorSide === 'assistant' && <StatusBadge tone="info">AI</StatusBadge>}
                {reply.authorSide === 'prentus' && <span className="thread-side">Prentus</span>}
                <time dateTime={reply.createdAt}>{formatThreadTime(reply.createdAt)}</time>
                {reply.corrected && <span className="thread-flag">Corrected by Prentus</span>}
                {reply.hidden && <StatusBadge tone="neutral">Hidden From Customer</StatusBadge>}
              </div>
              <p className="thread-reply-body">{reply.body}</p>
              {reply.authorSide === 'assistant' && (
                <>
                  {reply.sources.length > 0 && (
                    <div className="thread-sources">
                      <span>Sources</span>
                      <ul>
                        {reply.sources.map((source, index) => (
                          <li key={`${source.label}-${index}`}>
                            {source.href ? <a href={source.href}>{source.label}</a> : source.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <p className="thread-ai-note">This answer comes from an AI. It can be wrong. The Prentus team reads every thread.</p>
                </>
              )}
              {canModerate && reply.authorSide === 'assistant' && onModerate && (
                <div className="thread-moderate">
                  <Button type="button" variant="tertiary" compact onClick={() => setEditing(reply)}><PencilSimple size={14} aria-hidden="true" />Correct Answer</Button>
                  <Button type="button" variant="tertiary" compact onClick={() => void onModerate(reply.id, { hidden: !reply.hidden })}>
                    {reply.hidden ? <Eye size={14} aria-hidden="true" /> : <EyeOff size={14} aria-hidden="true" />}{reply.hidden ? 'Show to Customer' : 'Hide From Customer'}
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
      {assistantPending && (
        <p className="thread-pending" role="status"><SpinnerGap size={16} className="spin" aria-hidden="true" />Prentus Assistant is writing an answer. This can take up to a minute.</p>
      )}
      {canReply && onReply && (open
        ? (
          <form className="thread-form" onSubmit={(event) => void submit(event)} noValidate>
            <Field label="Your Reply" error={error || undefined} hint={`${trimmed.length.toLocaleString('en-US')} of ${THREAD_REPLY_MAX_CHARS.toLocaleString('en-US')} characters`}>
              <textarea id={fieldId} value={text} rows={3} maxLength={THREAD_REPLY_MAX_CHARS} autoFocus onChange={(event) => setText(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) void submit(event) }} />
            </Field>
            <div className="thread-form-actions">
              <Button type="submit" compact disabled={busy}>{busy && <SpinnerGap size={14} className="spin" aria-hidden="true" />}Send Reply</Button>
              <Button type="button" variant="secondary" compact disabled={busy} onClick={() => { setOpen(false); setError('') }}>Cancel</Button>
            </div>
          </form>
        )
        : <Button type="button" variant="tertiary" compact className="thread-reply-toggle" onClick={() => setOpen(true)}><Reply size={14} aria-hidden="true" />Reply</Button>)}
      {editing && onModerate && <CorrectDialog reply={editing} onClose={() => setEditing(null)} onSave={async (body) => { const saved = await onModerate(editing.id, { body }); if (saved) setEditing(null); return saved }} />}
    </div>
  )
}

function CorrectDialog({ reply, onClose, onSave }: { reply: ThreadReply; onClose: () => void; onSave: (body: string) => Promise<boolean> }) {
  const [text, setText] = useState(reply.body)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const formId = useId()
  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!text.trim()) { setError('Write the corrected answer.'); return }
    setBusy(true)
    await onSave(text.trim())
    setBusy(false)
  }
  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={!busy}
      busy={busy}
      title="Correct the Assistant Answer"
      description="The customer sees your text with the label Corrected by Prentus. The first answer stays in the Assistant log."
      footer={(
        <>
          <Button type="button" variant="secondary" disabled={busy} onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} disabled={busy}>{busy && <SpinnerGap size={16} className="spin" aria-hidden="true" />}Save Correction</Button>
        </>
      )}
    >
      <form id={formId} onSubmit={(event) => void submit(event)} noValidate>
        <Field label="Answer" required error={error || undefined}>
          <textarea value={text} rows={8} maxLength={ASSISTANT_CORRECTION_MAX_CHARS} data-autofocus onChange={(event) => setText(event.target.value)} />
        </Field>
      </form>
    </Dialog>
  )
}

/** A link that opens outside the portal, with the icon the portal uses for that. */
export function ExternalLink({ href, children, className = 'button button-secondary compact' }: { href: string; children: string; className?: string }) {
  return <a className={className} href={href} target="_blank" rel="noopener noreferrer">{children}<ArrowSquareOut size={14} aria-hidden="true" /><span className="ui-visually-hidden"> (opens in a new tab)</span></a>
}
